import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Zap } from "lucide-react";

export function EventsManagement({ game, onRefresh }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (game?.id) {
      fetchEvents();
    }
  }, [game?.id, game?.current_quarter]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/games/${game.id}/events`);
      setEvents(response.data);
    } catch (err) { console.error("EventsManagement:", err.message); } finally {
      setLoading(false);
    }
  };

  const handleGenerateEvents = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/games/${game.id}/events/generate`);
      toast.success(response.data.message);
      fetchEvents();
    } catch (error) {
      toast.error("Failed to generate events");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-semibold text-slate-900">{t("nav.events")}</h3>
          <p className="text-sm text-slate-500">Generate and manage special events for Q{game?.current_quarter}</p>
        </div>
        <Button onClick={handleGenerateEvents} disabled={generating || game?.status !== "active"} className="gap-2">
          <Zap className="w-4 h-4" />
          {generating ? t("common.loading") : t("instructor.generateEvents")}
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Zap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">No Events This Quarter</h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Click &ldquo;Generate Events&rdquo; to create special events that teams must respond to.
              Events add strategic challenges and unpredictability to the simulation.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {events.map(event => (
            <Card key={event.id} className="border-l-4 border-l-amber-500">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{event.title}</CardTitle>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                    event.event_type === "economic" ? "bg-amber-100 text-amber-700" :
                    event.event_type === "hr" ? "bg-blue-100 text-blue-700" :
                    event.event_type === "competitive" ? "bg-rose-100 text-rose-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>
                    {event.event_type}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-3">{event.description}</p>
                <div className="text-xs text-slate-500">
                  {event.choices?.length || 0} response options
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
